import React from "react";
import {
  formatChartHistoryFieldLabel,
  formatScalarValue,
  getChartHistoryRows,
  isArrayOfObjects,
} from "./chartHistoryUtils";
import { transcriptNoteModalColumnText } from "./supportStyles";

const sectionTextClass = `${transcriptNoteModalColumnText} whitespace-pre-wrap break-words`;

const leadingFieldHeadingPatterns = {
  allergies: /^\s*ALLERGIES:\s*(?:\r?\n)?/i,
  vitals: /^\s*VITALS:\s*(?:\r?\n)?/i,
  past_medical_history: /^\s*ACTIVE PROBLEMS:\s*(?:\r?\n)?/i,
  medications: /^\s*MEDICATIONS:\s*(?:\r?\n)?/i,
  lab_results: /^\s*LABORATORY RESULTS:\s*(?:\r?\n)?/i,
};

const stripRedundantFieldHeading = (field, text) => {
  if (typeof text !== "string") return text;
  const pattern = leadingFieldHeadingPatterns[field];
  return pattern ? text.replace(pattern, "").trimStart() : text;
};

const ChartHistorySectionHeading = ({ children, nested = false }) => (
  <div
    className={
      nested
        ? "mb-1.5 rounded-r border-l-2 border-l-violet-400 bg-violet-50/70 px-2 py-1"
        : "mb-2.5 rounded-r border-l-[3px] border-l-ktx-purple bg-slate-100 px-2.5 py-1.5"
    }
  >
    <span
      className={
        nested
          ? "text-xs font-semibold text-violet-900"
          : "text-sm font-semibold text-slate-800"
      }
    >
      {children}
    </span>
  </div>
);

const NestedObjectList = ({ rows }) => {
  if (rows.length === 0) {
    return <p className={`${transcriptNoteModalColumnText} text-gray-500`}>—</p>;
  }

  return (
    <div className="space-y-3">
      {rows.map((row, rowIndex) => (
        <div
          key={rowIndex}
          className="rounded-md border border-gray-200 bg-gray-50/60 px-2.5 py-2 space-y-2"
        >
          {Object.entries(row || {}).map(([key, value]) => (
            <div key={key}>
              <ChartHistorySectionHeading nested>
                {formatChartHistoryFieldLabel(key)}
              </ChartHistorySectionHeading>
              <p className={sectionTextClass}>{formatScalarValue(value)}</p>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const ChartHistorySectionContent = ({ field, raw, text, isNestedTable }) => {
  const displayText = stripRedundantFieldHeading(field, text);

  if (isNestedTable && isArrayOfObjects(raw)) {
    return <NestedObjectList rows={raw} />;
  }

  if (Array.isArray(raw)) {
    return (
      <div className="space-y-3">
        {raw.map((item, index) => (
          <p key={index} className={sectionTextClass}>
            {formatScalarValue(item)}
          </p>
        ))}
      </div>
    );
  }

  if (field === "lab_results" && typeof displayText === "string") {
    return (
      <pre className="max-w-full overflow-x-auto whitespace-pre rounded-md border border-gray-200 bg-slate-50 p-3 font-mono text-xs leading-relaxed text-slate-800">
        {displayText}
      </pre>
    );
  }

  return <p className={sectionTextClass}>{displayText}</p>;
};

const ChartHistoryTable = ({ chartHistory }) => {
  const rows = getChartHistoryRows(chartHistory);

  if (rows.length === 0) {
    return <p className={`${transcriptNoteModalColumnText} text-gray-500`}>No chart history on this patient.</p>;
  }

  return (
    <div className="space-y-5">
      {rows.map(({ field, label, raw, text, isNestedTable }) => (
        <section
          key={field}
          data-chart-history-field={field}
          className="border-b border-gray-200 pb-5 last:border-b-0 last:pb-0"
        >
          <ChartHistorySectionHeading>{label}</ChartHistorySectionHeading>
          <ChartHistorySectionContent
            field={field}
            raw={raw}
            text={text}
            isNestedTable={isNestedTable}
          />
        </section>
      ))}
    </div>
  );
};

export default ChartHistoryTable;
